import { Button } from '@crowdex/ui/components/button';
import type RFB from '@novnc/novnc';
import { Eye, LoaderCircle, Monitor, MousePointer2, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import { cloudViewUrl } from '@/lib/cloud-runtime/cloud-display';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';

type DisplayState =
  | { phase: 'connecting' }
  | { phase: 'connected' }
  | { phase: 'disconnected'; reason: string | null }
  | { phase: 'unavailable'; reason: string };

/** A sandbox started from an image without a desktop says so when its socket closes. */
const NO_DESKTOP = 'This sandbox has no desktop.';
const RETRY_DELAY_MS = 3_000;

/**
 * The cloud sandbox's desktop, live: what the agent runs there with a window (its browser among
 * them). Watched by default; the user takes control to type or click (a login, a captcha).
 * Connected only while shown.
 */
export function DesktopDisplayView({
  cloudRuntime,
  visible,
}: {
  cloudRuntime: CloudRuntime;
  visible: boolean;
}) {
  const screen = useRef<HTMLDivElement>(null);
  const client = useRef<RFB | null>(null);
  const [state, setState] = useState<DisplayState>({ phase: 'connecting' });
  const [control, setControl] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const controlRef = useRef(control);
  controlRef.current = control;

  // biome-ignore lint/correctness/useExhaustiveDependencies(attempt): the attempt counter reconnects (after a drop, or from the retry button).
  useEffect(() => {
    const target = screen.current;
    if (!visible || target === null) return;
    let disposed = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let socket: WebSocket | undefined;
    setState({ phase: 'connecting' });
    const failed = (reason: string | null) => {
      client.current = null;
      if (disposed) return;
      if (reason === NO_DESKTOP) {
        setState({ phase: 'unavailable', reason });
        return;
      }
      setState({ phase: 'disconnected', reason });
      // The sandbox may be restarting: try again while the view is shown.
      retry = setTimeout(() => setAttempt((value) => value + 1), RETRY_DELAY_MS);
    };
    // noVNC is loaded with the first desktop shown, not with every page; before the socket
    // opens, so that it reads the server's first bytes.
    void import('@novnc/novnc').then(
      ({ default: Rfb }) => {
        if (disposed) return;
        const opened = new WebSocket(cloudViewUrl(cloudRuntime, 'display'));
        socket = opened;
        let closeReason: string | null = null;
        opened.addEventListener('close', (event) => {
          closeReason = event.reason || null;
          if (client.current === null) failed(closeReason);
        });
        opened.addEventListener('open', () => {
          opened.send(JSON.stringify({ type: 'hello', token: cloudRuntime.token }));
          const rfb = new Rfb(target, opened, { shared: true });
          rfb.scaleViewport = true;
          rfb.viewOnly = !controlRef.current;
          rfb.background = 'transparent';
          client.current = rfb;
          rfb.addEventListener('connect', () => {
            if (!disposed) setState({ phase: 'connected' });
          });
          rfb.addEventListener('disconnect', () => failed(closeReason));
        });
      },
      () => failed(null),
    );
    return () => {
      disposed = true;
      clearTimeout(retry);
      const rfb = client.current;
      client.current = null;
      if (rfb !== null) rfb.disconnect();
      else socket?.close();
    };
  }, [cloudRuntime, visible, attempt]);

  useEffect(() => {
    const rfb = client.current;
    if (rfb === null) return;
    rfb.viewOnly = !control;
    if (control) rfb.focus();
  }, [control]);

  if (state.phase === 'unavailable')
    return (
      <PanelEmpty
        icon={Monitor}
        title="No desktop in this sandbox"
        text="It started before cloud sandboxes had one. A new conversation shows its desktop here."
      />
    );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-11 shrink-0 items-center gap-2 border-hairline-strong border-b px-4 text-xs">
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-muted-foreground">
          {state.phase === 'connected' ? (
            <span className="size-2 shrink-0 rounded-full bg-emerald-500" />
          ) : (
            <LoaderCircle className="size-3.5 shrink-0 animate-spin" />
          )}
          <span className="truncate">
            {state.phase === 'connected'
              ? control
                ? 'You have control: the agent may be acting too'
                : 'Watching the cloud desktop'
              : state.phase === 'connecting'
                ? 'Connecting to the cloud desktop…'
                : `Reconnecting${state.reason ? ` (${state.reason})` : ''}…`}
          </span>
        </span>
        {state.phase === 'disconnected' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAttempt((value) => value + 1)}
            aria-label="Reconnect"
          >
            <RefreshCw />
          </Button>
        )}
        <Button
          size="sm"
          variant={control ? 'secondary' : 'ghost'}
          disabled={state.phase !== 'connected'}
          onClick={() => setControl((value) => !value)}
        >
          {control ? <Eye /> : <MousePointer2 />}
          {control ? 'Release control' : 'Take control'}
        </Button>
      </div>
      <div
        ref={screen}
        data-testid="cloud-desktop-screen"
        className={`min-h-0 flex-1 overflow-hidden bg-muted/40 ${control ? '' : 'cursor-default'}`}
      />
    </div>
  );
}
